import React from "react";
import DoneTwoTone from "@mui/icons-material/DoneTwoTone";
import CloseTwoTone from "@mui/icons-material/CloseTwoTone";
import { IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DeleteOutline } from "@material-ui/icons";
import BookingGetters from "../../../store/onlineBooking/getters";
import i18n from "../../../config/i18n";
import { createTheme } from "@mui/material/styles";
import InsuranceRating from "./InsuranceRating";
import { formatTheme } from "../../../config/MuiColorThemes";

const InsurancePreview = ({
  insurance,
  insurances,
  specification,
  updateShowInsurance,
  previewModal,
  insurancespec,
}: {
  insurance?: any;
  insurances?: any;
  specification?: any;
  updateShowInsurance?: any;
  previewModal?: any;
  insurancespec?: any;
}) => {
  const { t: translate } = useTranslation();
  const lang = i18n.language;
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const data = textResource?.[lang]?.flatMap((item) => item.data || []) || [];

  const activeStarColor =
    ((createTheme(formatTheme()).palette as any)?.secondaryColor
      ?.main as string) || "#111";

  const getRating = (ins: {
    insuranceRating: null;
    details: any;
    Description: any;
  }) => {
    if (!ins) return null;
    if (ins.insuranceRating != null) return ins.insuranceRating;
    const key =
      previewModal === "selectedInsurance" ? ins.details : ins.Description;
    const match = Array.isArray(specification)
      ? specification.find((s) => s?.Description === key)
      : undefined;
    return match?.insuranceRating ?? null;
  };

  // Build the coverage-specification list for a single insurance object.
  const buildSpecifications = (ins) => {
    if (!ins || !specification) return [];
    const matchKey =
      previewModal === "selectedInsurance" ? ins.details : ins.Description;
    return specification.reduce((acc, e) => {
      if (e.Description === matchKey) {
        const currentInsurance = specification.find(
          (item) => item.Description === matchKey
        );
        const bookingTextEntries = Object.entries(e.bookingText || {})
          .filter(([key]) => key.includes(e.Description))
          .map(([key, value]) => {
            const cleanedKey = key.replace(`:${e.Description}`, "");
            const matchingData = data.find((item) => {
              return item.key.trim().toLowerCase() === cleanedKey.toLowerCase();
            });
            const matchingValueData = data.find((item) => {
              return (
                item.key.trim().toLowerCase() ===
                (value as string).trim().toLowerCase()
              );
            });
            const indicatorKey = `${cleanedKey.toUpperCase()}:${e.Description.toUpperCase()}`;
            const hasIndicator = currentInsurance?.indicator?.[indicatorKey];
            const isPositive = hasIndicator
              ? hasIndicator.toLowerCase() === "tick"
              : true;
            const updatedValue = matchingData
              ? `${matchingData.body} : ${matchingValueData?.body || value}`
              : `${cleanedKey} : ${value}`;
            return {
              text: updatedValue,
              isPositive,
              featureKey: cleanedKey,
              value,
            };
          });
        return [...acc, ...bookingTextEntries];
      }
      return acc;
    }, []);
  };

  const renderSpecList = (specs) => (
    <ul className="list-disc pl-5">
      {specs.map((spec, index) => {
        const [left, ...rest] = spec.text.split(":");
        const right = rest.join(":");
        const found = insurancespec.find(
          (item) =>
            item.Description.trim().toLowerCase() === left.trim().toLowerCase()
        );
        const bookingText = found ? found.BookingText : left;
        return (
          <li key={index} className="flex items-center">
            {spec.isPositive ? (
              <DoneTwoTone className="text-green-800 font-bold mr-2" />
            ) : (
              <CloseTwoTone className="text-red-800 mr-2" />
            )}
            {bookingText} :{right}
          </li>
        );
      })}
    </ul>
  );

  // Multiple included coverages: one header, every included coverage listed.
  if (previewModal !== "selectedInsurance" && Array.isArray(insurances)) {
    return (
      <div className="relative flex flex-wrap text-black animate__animated animate__zoomInRight p-8 shadow-lg rounded-lg bg-white">
        <div style={{ width: "-webkit-fill-available" }}>
          <h3 className="text-xl mb-2">{translate("your.plan.includes")} </h3>
        </div>
        {insurances.map((ins, insIndex) => (
          <div
            key={ins?.Id ?? insIndex}
            style={{ width: "-webkit-fill-available" }}
            className={insIndex > 0 ? "mt-4" : ""}
          >
            <h1 className="text-xl font-bold mb-2">{ins?.Description}</h1>
            <InsuranceRating
              rating={getRating(ins)}
              activeColor={activeStarColor}
              style={{ marginBottom: "8px" }}
            />
            {renderSpecList(buildSpecifications(ins))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative flex flex-wrap text-black animate__animated animate__zoomInRight p-8 shadow-lg rounded-lg bg-white">
      {previewModal === "selectedInsurance" && (
        <div className="absolute top-2 right-2">
          <IconButton>
            <DeleteOutline
              onClick={updateShowInsurance}
              className="text-red-500"
            />
          </IconButton>
        </div>
      )}
      {previewModal !== "selectedInsurance" && (
        <div style={{ width: "-webkit-fill-available" }}>
          <h3 className="text-xl mb-2">{translate("your.plan.includes")} </h3>
        </div>
      )}
      {previewModal === "selectedInsurance" ? (
        <div style={{ width: "-webkit-fill-available" }}>
          <h1 className="text-xl font-bold mb-2">{insurance.details}</h1>
        </div>
      ) : (
        <div style={{ width: "-webkit-fill-available" }}>
          <h1 className="text-xl font-bold mb-2">{insurance.Description}</h1>
        </div>
      )}
      <div style={{ width: "-webkit-fill-available" }}>
        <InsuranceRating
          rating={getRating(insurance)}
          activeColor={activeStarColor}
          style={{ marginBottom: "8px" }}
        />
      </div>
      <div>{renderSpecList(buildSpecifications(insurance))}</div>
    </div>
  );
};

export default InsurancePreview;
